/**
 * Pricing.tsx — 프리미엄 컬러풀 레이아웃
 * 5플랜 타임라인 · 기능 비교 sticky · ROI 풀블리드 · FAQ 2열
 */
import { useState, Fragment } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  FiCheck, FiMinus, FiArrowRight, FiTrendingUp, FiChevronDown,
  FiShield, FiLock, FiUsers, FiMessageSquare,
  FiClipboard, FiCamera, FiLayout, FiFileText, FiImage,
  FiGitBranch, FiTrello, FiDollarSign,
} from 'react-icons/fi'
import Button from '../../components/ui/Button'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../store/authStore'

/**
 * 요금제 카드의 구매 버튼이 실제로 가야 할 곳.
 *
 * 이전에는 전부 `/signup?plan=X` 로 갔는데 Signup 화면은 `plan` 을 읽지 않는다.
 * 그래서 "Pro 시작하기"를 누른 사람과 그냥 가입한 사람이 구분되지 않은 채 둘 다
 * 무료 사용자로 착지했고, 로그인 상태에서 누르면 이미 있는 계정의 빈 가입 폼이
 * 떠 결제 화면에 영영 도달하지 못했다(실측: 4개월간 셀프서브 결제 1건).
 * 무료 플랜만 예외 — 결제할 것이 없으므로 가입으로 보낸다.
 */
function planHref(planId: string, isAuthed: boolean): string {
  if (planId === 'free') return '/signup'
  if (planId === 'enterprise') return '/contact'
  const billing = `/dashboard/billing?plan=${planId}`
  return isAuthed ? billing : `/signup?plan=${planId}&redirect=${encodeURIComponent(billing)}`
}

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6 },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, delay: i * 0.1 },
})

// ── 플랜 정의 (gradient 복원) — 사용자 표시 텍스트는 t() 조회 키로 대체 ──
// 각 플랜의 name/desc/cta/features는 `pricing.plans.<id>*` 키로 렌더링된다.
type PlanSpec = {
  id: 'free' | 'starter' | 'pro' | 'business' | 'enterprise'
  name: string
  priceMonthly: number | null
  ctaVariant: 'outline' | 'primary'
  href: string
  gradient: string
  border: string
  accent: string
  glowColor: string
  featureCount: number
  highlight?: boolean
}

const PLANS: PlanSpec[] = [
  {
    id: 'free',
    name: 'Free',
    priceMonthly: 0,
    ctaVariant: 'outline',
    href: '/signup',
    gradient: 'from-zinc-800/60 to-zinc-900/40',
    border: 'border-zinc-600/30',
    accent: 'text-zinc-400',
    glowColor: 'rgba(100,116,139,0.08)',
    featureCount: 9,   // 자료 가져오기 항목 추가
  },
  {
    id: 'starter',
    name: 'Starter',
    priceMonthly: 4900,
    ctaVariant: 'outline',
    href: '/signup?plan=starter',
    gradient: 'from-amber-900/40 to-amber-950/30',
    border: 'border-amber-500/30',
    accent: 'text-amber-400',
    glowColor: 'rgba(59,130,246,0.10)',
    featureCount: 10,
  },
  {
    id: 'pro',
    name: 'Professional',
    priceMonthly: 30000,
    ctaVariant: 'primary',
    href: '/signup?plan=pro',
    gradient: 'from-amber-600/30 to-amber-600/20',
    border: 'border-amber-400/60',
    accent: 'text-amber-300',
    glowColor: 'rgba(6,182,212,0.15)',
    featureCount: 10,
    highlight: true,
  },
  {
    id: 'business',
    name: 'Business',
    priceMonthly: 90000,
    ctaVariant: 'outline',
    href: '/signup?plan=business',
    gradient: 'from-violet-900/40 to-purple-950/30',
    border: 'border-violet-500/30',
    accent: 'text-violet-400',
    glowColor: 'rgba(139,92,246,0.10)',
    featureCount: 11,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceMonthly: null,
    ctaVariant: 'outline',
    href: '/contact',
    gradient: 'from-amber-900/35 to-orange-950/25',
    border: 'border-amber-500/30',
    accent: 'text-amber-400',
    glowColor: 'rgba(245,158,11,0.08)',
    featureCount: 8,
  },
]

// ── 기능 비교 표 ──────────────────────────────────────────
type CellValue = boolean | string | number

// 테이블 값은 locale 키를 참조한다. 문자열 값은 t() 호출로, boolean은 체크/미지원 아이콘으로 렌더된다.
type RowSpec = {
  labelKey: string
  icon?: React.ComponentType<{ size?: number; className?: string }>
  values: CellValue[]
}

// 컬럼 순서: Free / Starter / Pro / Business / Enterprise
// 수치 출처: heinrich/ops/sql/004_plan_repricing_2026_05.sql §10.2 (챗봇=일 단위, 그 외=월 단위)
const COMPARISON_ROWS: { categoryKey: string; rows: RowSpec[] }[] = [
  {
    categoryKey: 'pricing.categories.chatbot',
    rows: [
      { labelKey: 'pricing.rows.chatbot', icon: FiMessageSquare, values: ['pricing.cellValues.perDay1', 'pricing.cellValues.perDay20', 'pricing.cellValues.perDay55', 'pricing.cellValues.perDay150', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.source', values: [true, true, true, true, true] },
      { labelKey: 'pricing.rows.multiturn', values: [false, true, true, true, true] },
    ],
  },
  {
    categoryKey: 'pricing.categories.docAutomation',
    rows: [
      { labelKey: 'pricing.rows.workerRoster', icon: FiShield, values: ['pricing.cellValues.people5', 'pricing.cellValues.people49', 'pricing.cellValues.people299', 'pricing.cellValues.people500', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.siteCount', values: ['pricing.cellValues.sites1', 'pricing.cellValues.sites1', 'pricing.cellValues.sites3', 'pricing.cellValues.sites10', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.jsa', icon: FiClipboard, values: ['pricing.cellValues.month1', 'pricing.cellValues.month12', 'pricing.cellValues.month60', 'pricing.cellValues.month200', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.tbmText', icon: FiFileText, values: ['pricing.cellValues.day1', 'pricing.cellValues.day3', 'pricing.cellValues.day15', 'pricing.cellValues.day40', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.tbmImage', icon: FiImage, values: ['pricing.cellValues.notAvailable', 'pricing.cellValues.month2', 'pricing.cellValues.month12', 'pricing.cellValues.month40', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.riskAssess', icon: FiShield, values: ['pricing.cellValues.month1', 'pricing.cellValues.month12', 'pricing.cellValues.month50', 'pricing.cellValues.month200', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.corrective', values: ['pricing.cellValues.month1', 'pricing.cellValues.month10', 'pricing.cellValues.month60', 'pricing.cellValues.month150', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.workPermit', values: ['pricing.cellValues.month1', 'pricing.cellValues.month15', 'pricing.cellValues.month120', 'pricing.cellValues.month400', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.accidentReport', values: ['pricing.cellValues.month1', 'pricing.cellValues.month3', 'pricing.cellValues.month25', 'pricing.cellValues.month50', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.eta', icon: FiGitBranch, values: ['pricing.cellValues.month1', 'pricing.cellValues.month5', 'pricing.cellValues.month40', 'pricing.cellValues.month80', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.fta', icon: FiTrello, values: ['pricing.cellValues.month1', 'pricing.cellValues.month5', 'pricing.cellValues.month40', 'pricing.cellValues.month80', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.safetyCost', icon: FiDollarSign, values: ['pricing.cellValues.month1', 'pricing.cellValues.month12', 'pricing.cellValues.month50', 'pricing.cellValues.month120', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.scenario', values: [false, false, true, true, true] },
    ],
  },
  {
    categoryKey: 'pricing.categories.imageAnalysis',
    rows: [
      { labelKey: 'pricing.rows.imageAnalysis', icon: FiCamera, values: ['pricing.cellValues.month1', 'pricing.cellValues.month15', 'pricing.cellValues.month100', 'pricing.cellValues.month250', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.lawMap', values: [false, true, true, true, true] },
    ],
  },
  {
    categoryKey: 'pricing.categories.safetyEducation',
    rows: [
      // PPTX 한도 (2026-08 개편): Free 0 / Starter 1 / Pro 5 / Business 16.
      //   1회당 AI 이미지 6.39장(실측) ≈ 631원 — 챗봇·영상과 함께 원가의 91% 를 차지한다.
      { labelKey: 'pricing.rows.safetyEducationPptx', icon: FiFileText, values: ['pricing.cellValues.notAvailable', 'pricing.cellValues.month1', 'pricing.cellValues.month5', 'pricing.cellValues.month16', 'pricing.cellValues.unlimited'] },
      // 영상: 1편 = 1법정시간 단위 (약 43분). 전 플랜 Gemini TTS 라 1편 원가 2,641원(실측 평균)
      //   → Free·Starter 는 미지원, Pro 2편 / Business 6편. 다국어 추가 시 영상 한도에서 가중치 차감
      // (자막만 +0.1편, 자막+음성 +0.3편, PPTX만 +0.1편, 풀 +0.4편).
      // 관리감독자·특별교육은 강사 의무 비율 제외하고 AI 영상 편수만 차감.
      { labelKey: 'pricing.rows.safetyEducationVideo', values: ['pricing.cellValues.notAvailable', 'pricing.cellValues.notAvailable', 'pricing.cellValues.month2', 'pricing.cellValues.month6', 'pricing.cellValues.unlimited'] },
      // Sprint 3 다국어 (외국인 근로자 4개국 — 영·베·중·인) — 산안법 §37 모국어 의무 대응
      { labelKey: 'pricing.rows.safetyEducationI18n', values: ['pricing.cellValues.notAvailable', 'pricing.cellValues.langSubtitleOnly', 'pricing.cellValues.lang4countriesVoice', 'pricing.cellValues.lang4countriesFull', 'pricing.cellValues.unlimited'] },
    ],
  },
  {
    categoryKey: 'pricing.categories.docOutput',
    rows: [
      { labelKey: 'pricing.rows.excelPdf', icon: FiLayout, values: [true, true, true, true, true] },
      { labelKey: 'pricing.rows.uploadForm', values: [false, false, false, 'pricing.cellValues.unlimited', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.fieldMap', values: [false, false, false, true, true] },
      { labelKey: 'pricing.rows.templateMake', values: [false, false, false, true, true] },
    ],
  },
  {
    // 협력사(도급) 안전관리 — 원청의 §61~66 도급의무 실행·증빙 (2026-07 요금 확정)
    categoryKey: 'pricing.categories.partner',
    rows: [
      { labelKey: 'pricing.rows.partnerLinks', icon: FiShield, values: ['pricing.cellValues.links1', 'pricing.cellValues.links2', 'pricing.cellValues.links5', 'pricing.cellValues.links15', 'pricing.cellValues.unlimited'] },
      { labelKey: 'pricing.rows.partnerTools', values: [true, true, true, true, true] },
      { labelKey: 'pricing.rows.partnerSeal', values: [true, true, true, true, true] },
      { labelKey: 'pricing.rows.partnerPack', values: [false, true, true, true, true] },
      { labelKey: 'pricing.rows.partnerLog', values: ['pricing.cellValues.days90', 'pricing.cellValues.years3', 'pricing.cellValues.years3', 'pricing.cellValues.years3', 'pricing.cellValues.years3'] },
    ],
  },
  {
    categoryKey: 'pricing.categories.security',
    rows: [
      { labelKey: 'pricing.rows.onPremise', icon: FiLock, values: [false, false, false, false, true] },
    ],
  },
  {
    categoryKey: 'pricing.categories.lawNotify',
    rows: [
      { labelKey: 'pricing.rows.globalStd', icon: FiShield, values: [true, true, true, true, true] },
      { labelKey: 'pricing.rows.dailyUpdate', values: [true, true, true, true, true] },
    ],
  },
  {
    categoryKey: 'pricing.categories.support',
    rows: [
      { labelKey: 'pricing.rows.supportChannel', values: ['pricing.cellValues.community', 'pricing.cellValues.email', 'pricing.cellValues.priorityEmail', 'pricing.cellValues.priorityEmail', 'pricing.cellValues.dedicated'] },
      { labelKey: 'pricing.rows.responseTime', values: ['pricing.cellValues.hours48plus', 'pricing.cellValues.hours24', 'pricing.cellValues.hours12', 'pricing.cellValues.hours4', 'pricing.cellValues.hours1'] },
      { labelKey: 'pricing.rows.onboarding', values: [false, false, false, false, true] },
    ],
  },
]

const PRICING_FAQ_KEYS: { q: string; a: string }[] = [
  { q: 'pricing.faqs.q1', a: 'pricing.faqs.a1' },
  { q: 'pricing.faqs.q2', a: 'pricing.faqs.a2' },
  { q: 'pricing.faqs.q3', a: 'pricing.faqs.a3' },
  { q: 'pricing.faqs.q4', a: 'pricing.faqs.a4' },
  { q: 'pricing.faqs.q5', a: 'pricing.faqs.a5' },
  { q: 'pricing.faqs.q6', a: 'pricing.faqs.a6' },
]

// ── ROI 계산기 ──────────────────────────────────────────
function RoiCalculator() {
  const { t } = useTranslation()
  const [sites, setSites] = useState(1)
  const [jsaPerMonth, setJsaPerMonth] = useState(10)

  const hoursSaved = Math.round(jsaPerMonth * sites * (45 - 5) / 60)
  const moneySaved = hoursSaved * 30000
  const roi = moneySaved > 0 ? Math.round(moneySaved / 30000 * 10) / 10 : 0

  return (
    <div className="relative rounded-lg overflow-hidden">
      {/* 배경 그라디언트 */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-600/20 via-amber-600/10 to-violet-600/10" aria-hidden="true" />
      <div className="absolute inset-0 border border-amber-500/30 rounded-lg" aria-hidden="true" />

      <div className="relative p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-md bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
            <FiTrendingUp className="text-amber-400" size={18} aria-hidden="true" />
          </div>
          <h3 className="text-lg font-bold text-white">{t('pricing.roiCalcTitle')}</h3>
          <span className="ml-auto text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-medium">{t('pricing.roiProNote')}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2" htmlFor="roi-sites">
              {t('pricing.roiSites')}
            </label>
            <div className="flex items-center gap-3">
              <input
                id="roi-sites"
                type="range"
                min={1} max={20} value={sites}
                onChange={(e) => setSites(Number(e.target.value))}
                className="flex-1 accent-amber-500"
                aria-label={t('pricing.roi.siteAria', { n: sites })}
              />
              <span className="text-white font-semibold tabular-nums w-12 text-right">{t('pricing.roiSiteUnit', { count: sites })}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2" htmlFor="roi-jsa">
              {t('pricing.roiJsa')}
            </label>
            <div className="flex items-center gap-3">
              <input
                id="roi-jsa"
                type="range"
                min={1} max={100} value={jsaPerMonth}
                onChange={(e) => setJsaPerMonth(Number(e.target.value))}
                className="flex-1 accent-amber-500"
                aria-label={t('pricing.roi.jsaAria', { n: jsaPerMonth })}
              />
              <span className="text-white font-semibold tabular-nums w-16 text-right">{t('pricing.roiJsaUnit', { count: jsaPerMonth })}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-5 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="font-mono tabular-nums text-3xl font-bold text-amber-400 mb-1">{hoursSaved}h</div>
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 mt-1">{t('pricing.roiSavedHours')}</div>
          </div>
          <div className="text-center p-5 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="font-mono tabular-nums text-2xl font-bold text-amber-400 mb-1">
              ₩{moneySaved.toLocaleString()}
            </div>
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 mt-1">{t('pricing.roiSavedMoney')}</div>
          </div>
          <div className="text-center p-5 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <div className="font-mono tabular-nums text-3xl font-bold text-violet-400 mb-1">{roi}x</div>
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 mt-1">{t('pricing.roiProRoi')}</div>
          </div>
        </div>

        <p className="text-xs text-zinc-600 mt-5">
          {t('pricing.roiFootnote')}
        </p>
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ──────────────────────────────────────────────
export default function Pricing() {
  const { t } = useTranslation()
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const displayPrice = (plan: PlanSpec) => {
    if (plan.priceMonthly === null) return t('pricing.negotiable')
    if (plan.priceMonthly === 0) return '₩0'
    return `₩${plan.priceMonthly.toLocaleString()}`
  }

  // plan id → translation key prefix (pricing.plans.<idName>)
  // free/pro/starter → `pricing.plans.freeName`, `pricing.plans.freeDesc`, `pricing.plans.freeCta`, `pricing.plans.freeFeature{i}`
  const planKeyPrefix = (id: PlanSpec['id']): string => id
  const planFeatures = (plan: PlanSpec): string[] => {
    const prefix = planKeyPrefix(plan.id)
    const out: string[] = []
    for (let i = 1; i <= plan.featureCount; i++) {
      out.push(t(`pricing.plans.${prefix}Feature${i}`))
    }
    return out
  }
  const planDesc = (plan: PlanSpec) => t(`pricing.plans.${planKeyPrefix(plan.id)}Desc`)
  const planCta = (plan: PlanSpec) => t(`pricing.plans.${planKeyPrefix(plan.id)}Cta`)

  const renderCell = (val: CellValue) => {
    if (val === true) return <FiCheck size={14} className="text-amber-400 mx-auto" aria-label={t('pricing.cellSupport')} />
    if (val === false) return <FiMinus size={14} className="text-zinc-700 mx-auto" aria-label={t('pricing.cellNoSupport')} />
    // string values starting with "pricing." are locale keys
    const rendered = typeof val === 'string' && val.startsWith('pricing.') ? t(val) : String(val)
    return <span className="text-xs text-zinc-400 tabular-nums">{rendered}</span>
  }

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>{t('pricing.pageTitle')}</title>
        <meta name="description" content={t('pricing.heroSubtitle')} />
        <link rel="canonical" href="https://heinrich.dasancms.co.kr/pricing/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:url" content="https://heinrich.dasancms.co.kr/pricing/" />
        <meta property="og:title" content={t('pricing.pageTitle')} />
        <meta property="og:description" content={t('pricing.heroSubtitle')} />
        <meta property="og:image" content="https://heinrich.dasancms.co.kr/og/pricing.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={t('pricing.pageTitle')} />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:locale:alternate" content="en_US" />
        <link rel="alternate" hrefLang="ko" href="https://heinrich.dasancms.co.kr/pricing/" />
        <link rel="alternate" hrefLang="en" href="https://heinrich.dasancms.co.kr/pricing/" />
        <link rel="alternate" hrefLang="x-default" href="https://heinrich.dasancms.co.kr/pricing/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('pricing.pageTitle')} />
        <meta name="twitter:description" content={t('pricing.heroSubtitle')} />
        <meta name="twitter:image" content="https://heinrich.dasancms.co.kr/og/pricing.png" />
      </Helmet>

      {/* ━━━━━━━━━━ 1. HERO — 차분한 에디토리얼 ━━━━━━━━━━ */}
      <section className="relative bg-zinc-950 pt-28 pb-20 border-b border-white/[0.06]" aria-label={t('pricing.heroAriaLabel')}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block text-xs font-mono text-zinc-500 tracking-wider mb-6">
            {t('pricing.badge')}
          </div>
          <h1
            className="font-bold tracking-tight leading-[1.1] mb-6 text-white"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 3.75rem)' }}
          >
            {t('pricing.heroTitleLine1')}{' '}
            <span className="text-zinc-400 font-medium">{t('pricing.heroTitleLine2')}</span>
          </h1>
          <p className="text-base leading-[1.7] text-zinc-400 max-w-2xl mx-auto mb-8">
            {t('pricing.heroSubtitle')}
          </p>
          <div className="inline-flex items-center gap-2 text-xs text-zinc-500">
            <span>{t('pricing.monthlyBadge')}</span>
            <span className="text-zinc-700">·</span>
            <span>{t('pricing.paymentsBadge')}</span>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ 2. 플랜 카드 — 통일 높이, 깔끔한 그리드 ━━━━━━━━━━ */}
      <section className="bg-zinc-950 pb-24" aria-labelledby="plans-title">
        <h2 id="plans-title" className="sr-only">{t('pricing.plansSrTitle')}</h2>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* 5개 카드 — 모두 동일 높이, Pro 만 ring 으로 강조 */}
          <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:overflow-visible sm:mx-0 sm:px-0">
            <div className="flex sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-5 min-w-[960px] sm:min-w-0 items-stretch">
              {PLANS.map((plan, idx) => {
                const isHighlight = 'highlight' in plan && plan.highlight === true
                return (
                  <motion.div
                    key={plan.id}
                    {...stagger(idx)}
                    className="relative flex-1"
                  >
                    <div
                      className={cn(
                        'relative h-full rounded-md border bg-zinc-900/40 p-6 flex flex-col transition-colors',
                        isHighlight
                          ? 'border-amber-500/50 bg-zinc-900/70'
                          : 'border-white/[0.08] hover:border-white/[0.16]',
                      )}
                    >
                      {/* 헤더 — 카테고리 + 이름 */}
                      <div className="mb-5">
                        <p className="text-[11px] font-mono text-zinc-500 tracking-wider">{planDesc(plan)}</p>
                        <h3 className={cn('text-lg font-semibold mt-1', isHighlight ? 'text-white' : 'text-zinc-200')}>{plan.name}</h3>
                      </div>

                      {/* 가격 영역 — 고정 높이 (카드 정렬 통일) */}
                      <div className="min-h-[68px] mb-5 pb-5 border-b border-white/[0.06]">
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-extrabold text-white tracking-tight">
                            {displayPrice(plan)}
                          </span>
                          {plan.priceMonthly !== null && plan.priceMonthly > 0 && (
                            <span className="text-sm text-zinc-400 font-medium">{t('pricing.perMonth')}</span>
                          )}
                        </div>
                        {plan.priceMonthly === 0 && (
                          <p className="text-[11px] text-zinc-500 mt-1">{t('pricing.freeNote')}</p>
                        )}
                        {plan.priceMonthly === null && (
                          <p className="text-[11px] text-zinc-500 mt-1">{t('pricing.negotiableNote')}</p>
                        )}
                      </div>

                      {/* 기능 리스트 — flex-1 로 남은 공간 채움 */}
                      <ul className="flex-1 flex flex-col gap-2 mb-6">
                        {planFeatures(plan).map((feat) => (
                          <li key={feat} className="flex items-start gap-2 text-[12.5px] leading-snug">
                            <FiCheck size={12} className="shrink-0 mt-0.5 text-zinc-500" aria-hidden="true" />
                            <span className="text-zinc-300">{feat}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA — 카드 하단 고정 */}
                      <Link to={planHref(plan.id, isAuthenticated)} className="mt-auto block">
                        <Button
                          variant={plan.ctaVariant}
                          size="md"
                          className="w-full"
                          aria-label={t('pricing.planCtaAriaFormat', { plan: plan.name, cta: planCta(plan) })}
                        >
                          {planCta(plan)}
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>

          <p className="text-center text-xs text-zinc-500 mt-8">
            {t('pricing.refundNote')}
          </p>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden="true" />

      {/* ━━━━━━━━━━ 3. 기능 비교 표 (sticky 헤더) ━━━━━━━━━━ */}
      <section className="py-24 lg:py-32 bg-[#060a14]" aria-labelledby="comparison-title">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-zinc-800/60 border border-white/[0.08] text-zinc-400 uppercase tracking-widest mb-5">
              {t('pricing.comparisonBadge')}
            </span>
            <h2
              id="comparison-title"
              className="font-bold tracking-tight text-white mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              {t('pricing.comparisonHeadline')}
            </h2>
            <p className="text-[15px] text-zinc-400">{t('pricing.comparisonDescription')}</p>
          </motion.div>

          <motion.div {...fadeInUp}>
            <div className="overflow-x-auto rounded-lg border border-white/[0.08] bg-zinc-900/40">
              <table className="w-full min-w-[700px]" role="table" aria-label={t('pricing.tableAria')}>
                <thead>
                  <tr className="bg-zinc-900">
                    <th className="text-left px-5 py-4 text-zinc-400 font-semibold text-xs w-48" scope="col">
                      {t('pricing.tableFeatureHeader')}
                    </th>
                    {PLANS.map((plan) => (
                      <th key={plan.id} className="px-5 py-4 text-center text-sm font-bold" scope="col">
                        <span className={plan.accent}>{plan.name}</span>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {COMPARISON_ROWS.map((group, gIdx) => (
                    <Fragment key={`cat-${group.categoryKey}`}>
                      {/* 카테고리 헤더 행 — 명확한 시각적 분리 */}
                      <tr className="bg-zinc-800/60">
                        <td
                          colSpan={6}
                          className={cn(
                            'px-5 py-3 text-[11px] font-bold text-zinc-200 uppercase tracking-[0.2em]',
                            gIdx === 0 && 'border-t border-white/[0.10]',
                          )}
                        >
                          {t(group.categoryKey)}
                        </td>
                      </tr>

                      {/* 카테고리 내 기능 행들 */}
                      {group.rows.map((row, rIdx) => (
                        <tr
                          key={row.labelKey}
                          className={cn(
                            'hover:bg-white/[0.02] transition-colors duration-200',
                            rIdx === 0 && 'border-t border-white/[0.06]',
                          )}
                        >
                          <td className="px-5 py-3.5 text-sm text-zinc-300 align-middle">
                            <span className="inline-flex items-center gap-2">
                              {row.icon && (
                                <row.icon size={13} className="text-zinc-500 shrink-0" aria-hidden="true" />
                              )}
                              <span>{t(row.labelKey)}</span>
                            </span>
                          </td>
                          {row.values.map((val, i) => (
                            <td
                              key={i}
                              className={cn(
                                'px-5 py-3.5 text-center align-middle',
                                i === 2 && 'bg-amber-500/[0.04]',
                                rIdx > 0 && 'border-t border-white/[0.04]',
                              )}
                            >
                              {renderCell(val)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ━━━━━━━━━━ 4. Enterprise 상세 ━━━━━━━━━━ */}
      <section className="py-24 lg:py-32 bg-zinc-950" aria-labelledby="enterprise-title">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-6">
                {t('pricing.enterpriseSection.badge')}
              </span>
              <h2
                id="enterprise-title"
                className="font-bold tracking-tight leading-tight mb-6"
                style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
              >
                <span className="text-white">{t('pricing.enterpriseSection.headline1')}</span>
                <br />
                <span className="text-amber-400">
                  {t('pricing.enterpriseSection.headline2')}
                </span>
              </h2>
              <p className="text-[15px] leading-[1.7] text-zinc-400 mb-6">
                {t('pricing.enterpriseSection.description')}
              </p>

              <div className="flex flex-col gap-4 mb-8">
                {[
                  { step: '01', titleKey: 'pricing.enterpriseSection.step1Title', descKey: 'pricing.enterpriseSection.step1Desc', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
                  { step: '02', titleKey: 'pricing.enterpriseSection.step2Title', descKey: 'pricing.enterpriseSection.step2Desc', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
                  { step: '03', titleKey: 'pricing.enterpriseSection.step3Title', descKey: 'pricing.enterpriseSection.step3Desc', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
                  { step: '04', titleKey: 'pricing.enterpriseSection.step4Title', descKey: 'pricing.enterpriseSection.step4Desc', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className={cn('w-9 h-9 rounded-md border flex items-center justify-center shrink-0 text-xs font-bold', item.bg, item.color)}>
                      {item.step}
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">{t(item.titleKey)}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{t(item.descKey)}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/contact">
                <Button variant="primary" size="lg" rightIcon={<FiArrowRight aria-hidden="true" />}>
                  {t('pricing.enterpriseSection.ctaContact')}
                </Button>
              </Link>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="grid grid-cols-1 gap-4">
                {[
                  {
                    icon: FiLock,
                    titleKey: 'pricing.enterpriseSection.card1Title',
                    descKey: 'pricing.enterpriseSection.card1Desc',
                    gradient: 'from-amber-600/20 to-amber-900/20',
                    border: 'border-amber-500/25',
                    iconColor: 'text-amber-400',
                    iconBg: 'bg-amber-500/15 border-amber-500/25',
                  },
                  {
                    icon: FiShield,
                    titleKey: 'pricing.enterpriseSection.card2Title',
                    descKey: 'pricing.enterpriseSection.card2Desc',
                    gradient: 'from-emerald-600/20 to-emerald-900/20',
                    border: 'border-emerald-500/25',
                    iconColor: 'text-emerald-400',
                    iconBg: 'bg-emerald-500/15 border-emerald-500/25',
                  },
                  {
                    icon: FiUsers,
                    titleKey: 'pricing.enterpriseSection.card3Title',
                    descKey: 'pricing.enterpriseSection.card3Desc',
                    gradient: 'from-violet-600/20 to-violet-900/20',
                    border: 'border-violet-500/25',
                    iconColor: 'text-violet-400',
                    iconBg: 'bg-violet-500/15 border-violet-500/25',
                  },
                ].map((item) => (
                  <div
                    key={item.titleKey}
                    className={cn(
                      'rounded-lg border p-5 flex items-start gap-4 bg-gradient-to-br transition-all duration-300',
                      item.gradient,
                      item.border,
                    )}
                  >
                    <div className={cn('h-10 w-10 rounded-md border flex items-center justify-center shrink-0', item.iconBg)}>
                      <item.icon size={16} className={item.iconColor} aria-hidden="true" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white mb-1">{t(item.titleKey)}</div>
                      <div className="text-xs text-zinc-400 leading-relaxed">{t(item.descKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ 5. ROI 계산기 — 풀블리드 카드 ━━━━━━━━━━ */}
      <section className="py-24 lg:py-32 bg-[#060a14]" aria-labelledby="roi-title">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-5">
              ROI
            </span>
            <h2
              id="roi-title"
              className="font-bold tracking-tight mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              <span className="text-amber-300">
                {t('pricing.roi.headline1')}
              </span>
              <span className="text-white">{t('pricing.roi.headline2')}</span>
            </h2>
            <p className="text-[15px] text-zinc-400">{t('pricing.roi.description')}</p>
          </motion.div>

          <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
            <RoiCalculator />
          </motion.div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden="true" />

      {/* ━━━━━━━━━━ 6. FAQ — 2열 ━━━━━━━━━━ */}
      <section className="py-24 lg:py-32 bg-zinc-950" aria-labelledby="pricing-faq-title">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-zinc-800/60 border border-white/[0.08] text-zinc-400 uppercase tracking-widest mb-5">
              {t('pricing.faqBadge')}
            </span>
            <h2
              id="pricing-faq-title"
              className="font-bold tracking-tight text-white"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              {t('pricing.faqTitleFull')}
            </h2>
          </motion.div>

          {/* 2열 그리드 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-0">
            {PRICING_FAQ_KEYS.map((faq, idx) => (
              <motion.div key={idx} {...stagger(idx % 3)} className="border-b border-white/[0.06] last:border-b-0">
                <button
                  className="w-full flex items-center justify-between gap-4 py-6 text-left transition-colors duration-200 group"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  aria-expanded={openFaq === idx}
                  aria-controls={`pfaq-answer-${idx}`}
                >
                  <span className="font-medium text-white text-sm leading-relaxed group-hover:text-amber-200 transition-colors">{t(faq.q)}</span>
                  <motion.span
                    animate={{ rotate: openFaq === idx ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="shrink-0 text-zinc-500 group-hover:text-amber-400 transition-colors"
                    aria-hidden="true"
                  >
                    <FiChevronDown size={16} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === idx && (
                    <motion.div
                      id={`pfaq-answer-${idx}`}
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="pb-6 text-[14px] text-zinc-400 leading-[1.75]">
                        {t(faq.a)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ 7. CTA — 풀블리드 그라디언트 ━━━━━━━━━━ */}
      <section
        className="py-24 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0d1b38 0%, #0c1628 40%, #0e0d2a 100%)' }}
        aria-label={t('pricing.finalCta.ariaLabel')}
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <motion.div
            className="absolute top-0 left-1/3 w-[40rem] h-[20rem] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)' }}
            animate={{ x: [0, 30, 0], y: [0, 15, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-0 right-1/4 w-[35rem] h-[20rem] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)' }}
            animate={{ x: [0, -20, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="font-bold tracking-tight mb-5 leading-tight"
              style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
            >
              <span className="text-white">{t('pricing.finalCta.headline1')}</span>{' '}
              <span className="text-white">
                {t('pricing.finalCta.headline2')}
              </span>
            </h2>
            <p className="text-[15px] text-zinc-400 mb-10">
              {t('pricing.finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup">
                <Button variant="primary" size="lg" rightIcon={<FiArrowRight aria-hidden="true" />} className="w-full sm:w-auto text-base">
                  {t('pricing.finalCta.freeAccount')}
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="w-full sm:w-auto text-base border-white/[0.20] hover:border-white/[0.35]">
                  {t('pricing.finalCta.enterpriseInquiry')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
